import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMobileAuth } from '../../context/MobileAuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { DatePicker } from '../../components/ui/date-picker';
import { Search, X, Filter, Home, CalendarRange } from 'lucide-react';
import { format, addDays, subDays } from 'date-fns';
import { GroupedJobTimeline } from '../../components/mobile/GroupedJobTimeline';
import MobileLayout from '../../components/mobile/MobileLayout';

const STATUS_CHIPS = [
  { value: 'sent', label: 'Awaiting ack', hint: 'Sent to me, not yet confirmed' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'docketed', label: 'Docketed' },
  { value: 'invoiced', label: 'Invoiced' },
];

const userLocalDate = (offset = 0) => {
  const d = new Date();
  d.setDate(d.getDate() + offset);
  return format(d, 'yyyy-MM-dd');
};

const MobileJobs = () => {
  const navigate = useNavigate();
  const { api, isTimesheetOnly, isPPD } = useMobileAuth();

  useEffect(() => {
    if (isTimesheetOnly() && !isPPD()) navigate('/mobile/dashboard', { replace: true });
  }, [isTimesheetOnly, isPPD, navigate]);

  // Tab: 'upcoming' (today + next 6) | 'history' (last 30 days)
  const [tab, setTab] = useState('upcoming');

  // Search controls
  const [searchType, setSearchType] = useState('text');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState([]); // empty = all
  const [dockOnly, setDockOnly] = useState('any'); // 'any' | 'has' | 'missing'
  const [customRange, setCustomRange] = useState(null); // {start, end} overrides tab defaults
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Data
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  // Monotonic request counter so a stale response can never overwrite a newer one.
  const requestSeqRef = useRef(0);

  // Compute the effective date window for the current tab (or custom range override).
  const window = (() => {
    if (customRange) return customRange;
    if (tab === 'history') {
      return { start: userLocalDate(-30), end: userLocalDate(0) };
    }
    return { start: userLocalDate(0), end: userLocalDate(6) };
  })();

  const fetchJobs = useCallback(async () => {
    const mySeq = ++requestSeqRef.current;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        start_date: window.start,
        end_date: window.end,
        search_type: searchType,
      });
      if (searchQuery.trim()) params.set('q', searchQuery.trim());
      if (statusFilter.length) params.set('status', statusFilter.join(','));
      if (dockOnly === 'has') params.set('has_docket', 'true');
      if (dockOnly === 'missing') params.set('has_docket', 'false');

      const res = await api.get(`/api/mobile/jobs/search?${params.toString()}`);
      // Drop stale responses — only the latest request's data may land in state.
      if (mySeq !== requestSeqRef.current) return;
      setJobs(res.data);
    } catch (e) {
      if (mySeq !== requestSeqRef.current) return;
      console.error('Failed to load jobs', e);
      setJobs([]);
    } finally {
      if (mySeq === requestSeqRef.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, window.start, window.end, searchType, searchQuery, statusFilter, dockOnly]);

  // Debounce input-driven refetches so typing doesn't hammer the API.
  useEffect(() => {
    const t = setTimeout(fetchJobs, searchQuery ? 300 : 0);
    return () => clearTimeout(t);
  }, [fetchJobs, searchQuery]);

  const toggleStatus = (val) => {
    setStatusFilter((prev) =>
      prev.includes(val) ? prev.filter((s) => s !== val) : [...prev, val]
    );
  };

  const resetSearch = () => {
    setSearchType('text');
    setSearchQuery('');
    setStatusFilter([]);
    setDockOnly('any');
    setCustomRange(null);
  };

  const hasActiveFilters =
    !!searchQuery || statusFilter.length > 0 || dockOnly !== 'any' || customRange;

  const placeholderByType = {
    text: 'Search customer, site or job #',
    job_number: 'Job number (e.g. 51)',
    customer: 'Customer name',
    site: 'Site / location',
  };

  return (
    <MobileLayout>
      {/* Header */}
      <div className="bg-[var(--m-bg-card)] border-b border-[var(--m-border)] sticky top-0 z-10 safe-top">
        <div className="px-4 py-3 flex items-center justify-between">
          <h1 className="text-[var(--m-text)] font-bold text-lg">My Jobs</h1>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/mobile/dashboard')}
            data-testid="mobile-jobs-home-btn"
          >
            <Home className="h-5 w-5 text-[var(--m-text-secondary)]" />
          </Button>
        </div>

        {/* Tabs */}
        <div className="px-4 pb-3">
          <Tabs value={tab} onValueChange={(v) => { setTab(v); setCustomRange(null); }}>
            <TabsList className="bg-[var(--m-bg)] grid grid-cols-2 w-full" data-testid="mobile-jobs-tabs">
              <TabsTrigger value="upcoming" data-testid="tab-upcoming">Upcoming · 7 days</TabsTrigger>
              <TabsTrigger value="history" data-testid="tab-history">History · 30 days</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Search row */}
        <div className="px-4 pb-3 flex items-center gap-2">
          <Select value={searchType} onValueChange={setSearchType}>
            <SelectTrigger className="w-32 bg-[var(--m-bg)] border-[var(--m-border)] text-[var(--m-text)]" data-testid="search-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[var(--m-bg-card)] border-[var(--m-border)] text-[var(--m-text)]">
              <SelectItem value="text">Text</SelectItem>
              <SelectItem value="job_number">Job #</SelectItem>
              <SelectItem value="customer">Customer</SelectItem>
              <SelectItem value="site">Site</SelectItem>
            </SelectContent>
          </Select>
          <div className="relative flex-1">
            <Search className="h-4 w-4 absolute left-2 top-1/2 -translate-y-1/2 text-[var(--m-text-muted)]" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={placeholderByType[searchType]}
              className="pl-8 pr-8 bg-[var(--m-bg)] border-[var(--m-border)] text-[var(--m-text)]"
              data-testid="search-input"
              inputMode={searchType === 'job_number' ? 'numeric' : 'text'}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--m-text-muted)]"
                data-testid="search-clear"
                aria-label="Clear search"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <Popover open={filtersOpen} onOpenChange={setFiltersOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className={`border-[var(--m-border)] ${hasActiveFilters ? 'bg-[#ffd108] text-black border-[#ffd108]' : 'bg-[var(--m-bg)] text-[var(--m-text-secondary)]'}`}
                data-testid="filters-btn"
                aria-label="More filters"
              >
                <Filter className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 bg-[var(--m-bg-card)] border-[var(--m-border)] text-[var(--m-text)]" align="end">
              <div className="space-y-4">
                <div>
                  <p className="text-xs text-[var(--m-text-secondary)] mb-2">Status</p>
                  <div className="flex flex-wrap gap-2">
                    {STATUS_CHIPS.map((s) => {
                      const active = statusFilter.includes(s.value);
                      return (
                        <button
                          key={s.value}
                          onClick={() => toggleStatus(s.value)}
                          className={`text-xs px-3 py-1 rounded-full border transition ${active
                            ? 'bg-[#ffd108] text-black border-[#ffd108]'
                            : 'bg-transparent text-[var(--m-text-body)] border-[var(--m-border-light)] hover:border-gray-400'
                          }`}
                          data-testid={`status-chip-${s.value}`}
                        >
                          {s.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <p className="text-xs text-[var(--m-text-secondary)] mb-2">Docket</p>
                  <div className="flex gap-2">
                    {[
                      { v: 'any', l: 'Any' },
                      { v: 'has', l: 'Has docket' },
                      { v: 'missing', l: 'No docket' },
                    ].map((o) => (
                      <button
                        key={o.v}
                        onClick={() => setDockOnly(o.v)}
                        className={`text-xs px-3 py-1 rounded-full border transition ${dockOnly === o.v
                          ? 'bg-[#ffd108] text-black border-[#ffd108]'
                          : 'bg-transparent text-[var(--m-text-body)] border-[var(--m-border-light)] hover:border-gray-400'
                        }`}
                        data-testid={`docket-chip-${o.v}`}
                      >
                        {o.l}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="text-xs text-[var(--m-text-secondary)] mb-2 flex items-center gap-1">
                    <CalendarRange className="h-3 w-3" /> Custom date range
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <DatePicker
                      value={customRange?.start || window.start}
                      onChange={(d) => setCustomRange((prev) => ({
                        start: d,
                        end: prev?.end || window.end,
                      }))}
                      className="w-full bg-[var(--m-bg)] border-[var(--m-border)] text-[var(--m-text)]"
                      testId="range-start"
                    />
                    <DatePicker
                      value={customRange?.end || window.end}
                      onChange={(d) => setCustomRange((prev) => ({
                        start: prev?.start || window.start,
                        end: d,
                      }))}
                      className="w-full bg-[var(--m-bg)] border-[var(--m-border)] text-[var(--m-text)]"
                      testId="range-end"
                    />
                  </div>
                </div>

                <div className="flex justify-between pt-2 border-t border-[var(--m-border)]">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-[var(--m-text-secondary)]"
                    onClick={resetSearch}
                    data-testid="filters-reset"
                  >
                    Reset
                  </Button>
                  <Button
                    size="sm"
                    className="bg-[#ffd108] text-black hover:bg-[#e6bd00]"
                    onClick={() => setFiltersOpen(false)}
                    data-testid="filters-done"
                  >
                    Done
                  </Button>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* Summary line */}
        <div className="px-4 pb-3 text-xs text-[var(--m-text-muted)] flex items-center justify-between">
          <span data-testid="summary-count">
            {loading ? 'Loading…' : `${jobs.length} ${jobs.length === 1 ? 'result' : 'results'} · ${window.start} → ${window.end}`}
          </span>
          {hasActiveFilters && !loading && (
            <button
              onClick={resetSearch}
              className="text-[#ffd108] hover:underline"
              data-testid="clear-filters"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      <div className="px-4 py-4">
        <GroupedJobTimeline
          jobs={jobs}
          loading={loading}
          emptyMessage={
            hasActiveFilters
              ? 'No jobs match those filters.'
              : tab === 'history'
                ? "You've got no jobs in the last 30 days."
                : "You're clear for the next 7 days. 🎉"
          }
          onJobClick={(job) =>
            navigate(`/mobile/jobs/${job.id}?date=${job.viewing_date || userLocalDate(0)}`)
          }
          compactFuture={tab !== 'history'}
        />
      </div>
    </MobileLayout>
  );
};

export default MobileJobs;
